import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { toast } from "@grandvisionhq/common";
import { useLink } from "@grandvisionhq/frk-routing";
import { useSync } from "@grandvisionhq/lib-utils-w3c";
import { WithUserData } from "@grandvisionhq/my-account";
import { useFeatureToggles, useIntl, useDispatch, useSelector } from "@grandvisionhq/state";
import { init } from "../../actions.js";
import { resetMyAccountValidation } from "./actions.js";
import { useFormPrefill } from "../hooks/StepShipping/hooks/use-form-prefill.js";
import { useInvalidShippingStep } from "../hooks/StepShipping/hooks/use-invalid-shipping-step.js";
import { useShippingStepValidation } from "../hooks/StepShipping/hooks/use-shipping-step-validation.js";
import { useUpdatedShippingMethods } from "../hooks/StepShipping/hooks/use-updated-shipping-methods.js";
import { isPrefillFinished } from "./selectors.js";
import { StepShipping } from "./StepShipping.js";
import { StepNames } from "../../models/models/shared.js";
import { useCheckoutSettings } from "../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const StepShippingContainer = ({
  onNextStep,
  onPrevStep,
  shippingMethodsAsides,
  userData
}) => {
  const checkoutSettings = useCheckoutSettings();
  useFormPrefill(userData);
  const { getLabel } = useIntl();
  const dispatch = useDispatch();
  const { loading: isCartLoading } = Context.useCart();
  const isPrefillFinished$1 = useSelector(isPrefillFinished);
  const [isOptInsFetching, setIsOptInsFetching] = React.useState(true);
  const [isServiceStoreUpdating, setIsServiceStoreUpdating] = React.useState(true);
  const shouldShowLoader = React.useMemo(
    () => (isOptInsFetching || !isPrefillFinished$1) && !isCartLoading,
    [isCartLoading, isOptInsFetching, isPrefillFinished$1]
  );
  const isFirstStep = React.useMemo(
    () => Object.keys(checkoutSettings?.routes ?? {})[0] === StepNames.Shipping,
    [checkoutSettings?.routes]
  );
  const isCreateAccountRequired = Boolean(
    checkoutSettings.account?.implicitAccountCreation || checkoutSettings.account?.createAccountRequired
  );
  const { onClick: navigateToCart } = useLink({ route: "cart.overview" }, { replace: true }) || {};
  const broadCastChannel = useSync("new-cart-created").getChannel();
  broadCastChannel.onmessage = () => {
    toast.failed("cart.errors.cartNotInActiveState");
    navigateToCart?.();
  };
  const { isStepShippingValid } = useShippingStepValidation();
  const { handleInvalidStepShipping } = useInvalidShippingStep();
  const {
    isShippingMethodStateValid,
    handleUpdateShippingMethodsState,
    shouldShowUpdatedShippingMethodsModal,
    onCloseUpdatedShippingMethodsModal
  } = useUpdatedShippingMethods({ onNextStep, isServiceStoreUpdating });
  React.useEffect(() => {
    if (!isCreateAccountRequired) {
      dispatch(resetMyAccountValidation());
    }
  }, [isCreateAccountRequired]);
  React.useEffect(() => {
    dispatch(init());
  }, [dispatch]);
  const handleOptInsLoading = React.useCallback(
    ({ isOptInsLoading }) => {
      setIsOptInsFetching(isOptInsLoading);
    },
    []
  );
  const handleServiceStoreLoading = React.useCallback(
    ({ isServiceStoreLoading }) => {
      setIsServiceStoreUpdating(isServiceStoreLoading);
    },
    []
  );
  const handleNextStep = React.useCallback(
    (bypassValidation) => {
      if (!isStepShippingValid() && !bypassValidation) {
        handleInvalidStepShipping();
      } else if (!isShippingMethodStateValid() && !bypassValidation) {
        handleUpdateShippingMethodsState();
        return;
      } else {
        onNextStep();
      }
    },
    [
      handleUpdateShippingMethodsState,
      handleInvalidStepShipping,
      isShippingMethodStateValid,
      isStepShippingValid,
      onNextStep
    ]
  );
  const labels = React.useMemo(
    () => ({
      generic: {
        title: getLabel("checkout.generic.title")
      },
      customerType: {
        private: getLabel("checkout.customerType.private"),
        company: getLabel("checkout.customerType.company")
      },
      shipmentMethodTitle: getLabel("checkout.shipment.method.title"),
      serviceStoreTitle: getLabel("checkout.serviceStore.title"),
      serviceStoreSubtitle: getLabel("checkout.serviceStore.subtitle"),
      alternativeShippingMethodTitle: getLabel("checkout.shipment.method.alternative.modal.title")
    }),
    [getLabel]
  );
  return /* @__PURE__ */ React.createElement(
    StepShipping,
    {
      labels,
      onNextStep: handleNextStep,
      onPrevStep,
      isFirstStep,
      shippingMethodsAsides,
      shouldShowLoader,
      handleOptInsLoading,
      handleServiceStoreLoading,
      shouldShowUpdatedShippingMethodsModal,
      onCloseUpdatedShippingMethodsModal
    }
  );
};
const StepShippingContainerWithUserData = WithUserData(StepShippingContainer);
const StepShippingContainerWithMyAccount = ({ ...props }) => {
  const { isMyAccountEnabled } = useFeatureToggles();
  return /* @__PURE__ */ React.createElement(StepShippingContainerWithUserData, { ...props, isMyAccountEnabled });
};
export {
  StepShippingContainerWithMyAccount as default
};
//# sourceMappingURL=StepShippingContainer.js.map
